import PropTypes from 'prop-types';
import { useState } from 'react';
import { useI18n } from '../../../../context/I18nContext';
import { useModal } from '../../../../context/ModalContext';
import { useMatrixClient } from '../../../../context/MatrixContext';
import { useLoading } from '../../../../context/LoadingContext';
import SimpleTrans from '../../../Trans/SimpleTrans';
import ContactList from '../../../ContactList';
import RightSidebar from '../../../RightSidebar/RightSidebar';
import RoomAvatar from '../../../RoomAvatar';
import { exportConversationHistory } from '../../../../utils/conversationExport';
import logger from '../../../../utils/logger';
import { EXPORT_MESSAGE_LIMIT } from '../../../../constants/roomSettings';
import { usePushRules, PushRuleState } from '../../../../hooks/usePushRules';
import './Details.css';

const ServerNoticesInfo = () => {
  const { t } = useI18n();

  return (
    <div className="chatview-details-block">
      <div className="chatview-details-block-header no-top-border">
        {t('ConversationDetailsView.serverNoticesTitle', 'Server Notices')}
      </div>
      <div className="chatview-details-server-notice-info">
        <div className="chatview-details-server-notice-icon">
          <ion-icon name="information-circle-outline" />
        </div>
        <p className="chatview-details-server-notice-text">
          {t('ConversationDetailsView.serverNoticesDescription',
            'This room was created by the server to send you important notifications and system messages. You cannot send messages in this room.',
          )}
        </p>
      </div>
    </div>
  );
};

const DetailsHeader = ({
  id = null,
  title = '',
  topic = '',
  avatars = [],
  onEditClick = () => {},
  canEdit = false,
}) => {
  const { t } = useI18n();

  return (
    <div
      className={(canEdit) ? 'chatview-details-header clickable' : 'chatview-details-header'}
      onClick={(canEdit) ? onEditClick : undefined}
    >
      <div className="chatview-details-avatars">
        <RoomAvatar
          avatarUrls={avatars}
          placeholderText={title || '?'}
          roomId={id}
        />
      </div>
      <div className="chatview-details-title-container">
        <span className="chatview-details-title">{t('ConversationDetailsView.name')}: {title}</span>
        {topic && <span className="chatview-details-topic">{t('ConversationDetailsView.topic')}: {topic}</span>}
                

        <span className="chatview-details-id">{t('ConversationDetailsView.conversationID') + `: ${id}`}</span>
        
      </div>
      {
        canEdit && (
          <div className="chatview-details-edit-icon">
            <ion-icon name="create-outline" />
          </div>
        )
      }
    </div>
  );
};

DetailsHeader.propTypes = {
  id: PropTypes.number,
  title: PropTypes.string,
  topic: PropTypes.string,
  avatars: PropTypes.arrayOf(PropTypes.string),
  onEditClick: PropTypes.func,
  canEdit: PropTypes.bool,
};

const ActionsBlock = ({
  roomId = null,
  onLeaveClick = () => {},
  onAdvancedSettingsClick = () => {},
  canConfigure = false,
  isServerNotices = false,
}) => {
  const { t } = useI18n();
  const { showConfirm, showError } = useModal();
  const client = useMatrixClient();
  const { showLoading, hideLoading } = useLoading();
  const [hide, setHide] = useState(false);
  const { getRoomState, setRoomState } = usePushRules();
  const [isMuteUpdating, setIsMuteUpdating] = useState(false);

  // Get current mute state
  const currentState = roomId ? getRoomState(roomId) : PushRuleState.NOTIFY;
  const isMuted = currentState === PushRuleState.DONT_NOTIFY || currentState === PushRuleState.MENTIONS_ONLY;

  const handleMuteClick = async (e) => {
    e.stopPropagation();
    if (isMuteUpdating || !roomId) return;

    setIsMuteUpdating(true);
    try {
      const newState = isMuted ? PushRuleState.NOTIFY : PushRuleState.DONT_NOTIFY;
      await setRoomState(roomId, newState);
      logger.info('[ActionsBlock] Toggled mute state:', { roomId, newState });
    } catch (error) {
      logger.error('[ActionsBlock] Failed to toggle mute:', error);
      showError(
        t('ConversationDetailsView.muteError', 'Mute Failed'),
        t('ConversationDetailsView.muteErrorMessage', 'Could not change notification settings. Please try again.'),
      );
    } finally {
      setIsMuteUpdating(false);
    }
  };

  const handleLeaveClick = (e) => {
    e.stopPropagation();
    showConfirm(
      t('ConversationDetailsView.leaveRoomTitle'),
      t('ConversationDetailsView.leaveRoomMessage'),
      onLeaveClick, // onConfirm
      null, // onCancel - just close modal
    );
  };

  const handleExportClick = async (e) => {
    e.stopPropagation();

    if (!roomId) {
      showError(t('ConversationDetailsView.exportFailed'), t('ConversationDetailsView.exportFailedNoRoom'));
      return;
    }

    try {
      showLoading();
      await exportConversationHistory(client, roomId, EXPORT_MESSAGE_LIMIT);
      hideLoading();
      logger.info('[ActionsBlock] Conversation exported successfully');
    } catch (error) {
      hideLoading();
      logger.error('[ActionsBlock] Export failed:', error);
      showError(
        t('ConversationDetailsView.exportFailed'),
        t('ConversationDetailsView.exportFailedError', { error: error.message }),
      );
    }
  };

  if (isServerNotices) {
    return null;
  }

  return (
    <div className="chatview-details-block">
      <div
        className="chatview-details-block-header no-top-border"
        onClick={() => setHide(!hide)}
      >
        {t('ConversationDetailsView.actions')}
      </div>
      {
        !hide && (
          <>
            <div
              className={`chatview-details-block-item ${isMuteUpdating ? 'disabled' : ''}`}
              onClick={handleMuteClick}
            >
              <div className="chatview-details-block-item-icon">
                <ion-icon name={isMuted ? 'notifications-off-outline' : 'notifications-outline'} />
              </div>
              <span className="chatview-details-block-item-text">
                {isMuted
                  ? t('ConversationDetailsView.unmute', 'Unmute')
                  : t('ConversationDetailsView.mute', 'Mute')
                }
              </span>
            </div>
            <div className="chatview-details-block-item" onClick={handleExportClick}>
              <div className="chatview-details-block-item-icon">
                <i className="fas fa-download" />
              </div>
              <span className="chatview-details-block-item-text">{t('ConversationDetailsView.export')}</span>
            </div>
            <div className="chatview-details-block-item" onClick={handleLeaveClick}>
              <div className="chatview-details-block-item-icon">
                <i className="fas fa-sign-out-alt" />
              </div>
              <span className="chatview-details-block-item-text">{t('ConversationDetailsView.leave')}</span>
            </div>
            <div className="chatview-details-block-item" onClick={onAdvancedSettingsClick}>
              <div className="chatview-details-block-item-icon">
                <ion-icon name="settings-outline" />
              </div>
              <span className="chatview-details-block-item-text">
                {t('ConversationDetailsView.configureRoomSettings')}
              </span>
            </div>
          </>
        )
      }
    </div>
  );
};

ActionsBlock.propTypes = {
  roomId: PropTypes.string,
  onLeaveClick: PropTypes.func,
  onAdvancedSettingsClick: PropTypes.func,
  canConfigure: PropTypes.bool,
  isServerNotices: PropTypes.bool,
};

const ParticipantsBlock = ({
  participants = [],
  onEditClick = () => {},
  onContactClick = () => {},
  canEdit = false,
}) => {
  const { t } = useI18n();
  const [hide, setHide] = useState(false);

  return (
    <div className="chatview-details-block">
      <div className="chatview-details-block-header" onClick={() => setHide(!hide)}>
        <SimpleTrans
          i18nKey="ConversationDetailsView.xPeopleInConversation"
          defaults="<0>{numPar}</0> {numPar, plural, =1 {Person} other {People}} in this conversation"
          components={[<span key="count" style={{ color: 'red', fontWeight: 'bold' }} />]}
          values={{ numPar: participants.length }}
        />
      </div>
      {
        !hide && canEdit && (
          <div className="chatview-details-block-item" onClick={onEditClick}>
            <div className="chatview-details-block-item-icon">
              <ion-icon name="add-outline" />
            </div>
            <span className="chatview-details-block-item-text">{t('ConversationDetailsView.editParticipants')}</span>
          </div>
        )
      }
      {
        !hide &&
        <ContactList
          contacts={participants}
          isLoading={false} 
          onSelectContact={(contactId) => onContactClick(contactId)}
        />
      }
    </div>
  );
};

ParticipantsBlock.propTypes = {
  participants: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
  onEditClick: PropTypes.func,
  onContactClick: PropTypes.func,
  canEdit: PropTypes.bool,
}; 

const Details = ({
  id = null,
  title = '',
  topic = '',
  avatars = [],
  participants = [],
  onEditClick,
  onEditParticipantsClick,
  onContactClick,
  onLeaveRoom,
  onAdvancedSettingsClick,
  canEdit = false,
  canConfigure = false,
  onClose = () => {},
  isServerNotices = false,
}) => {
  
  const headerContent = (
    <DetailsHeader
      id={id}
      title={title}
      topic={topic}
      avatars={avatars}
      onEditClick={!isServerNotices ? onEditClick : undefined}
      canEdit={canEdit && !isServerNotices}
    />
  );

  return (
    <RightSidebar
      isOpen={true}
      onClose={onClose}
      headerContent={headerContent}
      showCloseButton={false}
      className="chatview-details"
      closeOnClickOutside={false}
    >
      {isServerNotices ? (
        <ServerNoticesInfo />
      ) : (
        <>
          <ActionsBlock
            roomId={id}
            onLeaveClick={onLeaveRoom}
            onAdvancedSettingsClick={onAdvancedSettingsClick}
            canConfigure={canConfigure}
            isServerNotices={isServerNotices}
          />
          <ParticipantsBlock
            participants={participants}
            onEditClick={onEditParticipantsClick || onEditClick}
            canEdit={canEdit}
            onContactClick={(contactId) => onContactClick(contactId)}
          />
        </>
      )}
    </RightSidebar>
  );
};

Details.propTypes = {
  id: PropTypes.number,
  title: PropTypes.string,
  topic: PropTypes.string,
  avatars: PropTypes.arrayOf(PropTypes.string),
  participants: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
  onEditClick: PropTypes.func.isRequired,
  onEditParticipantsClick: PropTypes.func,
  onLeaveRoom: PropTypes.func.isRequired,
  onContactClick: PropTypes.func.isRequired,
  onAdvancedSettingsClick: PropTypes.func,
  canEdit: PropTypes.bool,
  canConfigure: PropTypes.bool,
  onClose: PropTypes.func,
  isServerNotices: PropTypes.bool,
};

export default Details;
